import React, { useMemo } from "react";
import {
  TrendUp, TrendDown, Minus, Fire, Clock, Pill, Moon, Star, Lightning, Person,
} from "@phosphor-icons/react";
import { computeInsights, fmtHours } from "@/lib/symptomDiary";

/**
 * VA Evidence Insights — the chronic-condition diary analytics pack:
 * overall trend, flare frequency by month, flare length + missed time,
 * medication effectiveness, sleep-vs-severity correlation, worst days.
 */
export default function SymptomInsightsPanel({ items = [] }) {
  const ins = useMemo(() => computeInsights(items), [items]);
  if (!items.length) return null;

  return (
    <div className="sym-charts-grid" data-testid="symptom-insights">
      <Card title="Overall trend" sub="Severity direction, last 60 days" icon={trendIcon(ins.trend.direction)} testid="insight-trend">
        <div style={{ width: "100%" }}>
          <span className={`sym-trend-badge sym-trend-badge--${ins.trend.direction}`} data-testid="insight-trend-badge">
            {trendIconEl(ins.trend.direction)} {trendLabel(ins.trend.direction)}
          </span>
          <div style={{ fontSize: 11, color: "var(--text-secondary, #64748B)", marginTop: 8 }}>
            {ins.trend.direction === "insufficient"
              ? "Log entries on 5+ different days to unlock the trend."
              : `Severity is changing about ${Math.abs(ins.trend.slopePerWeek).toFixed(2)} points/week across ${ins.trend.daysWithData} logged days.`}
          </div>
          {ins.worstDays.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.08em", color: "var(--text-tertiary, #94A3B8)", marginBottom: 4 }}>
                WORST DAYS
              </div>
              {ins.worstDays.slice(0, 3).map((d, i) => (
                <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 11, marginBottom: 3 }} data-testid={`insight-worst-day-${i}`}>
                  {d.worstDay && <Star size={10} weight="fill" style={{ color: "var(--gold-500, #C9A24A)" }} />}
                  <span style={{ fontFamily: "JetBrains Mono, monospace", fontWeight: 800, color: sevColor(d.severity) }}>{d.severity}/10</span>
                  <span style={{ color: "var(--text-secondary)" }}>{shortDate(d.date)}</span>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.symptom}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      <Card title="Flare frequency" sub="Flare-ups by month, last 6 months" icon={Fire} testid="insight-flares">
        <MonthBars rows={ins.flaresByMonth} />
      </Card>

      <Card title="Flares & missed time" sub="Duration + missed work / PT / duty" icon={Clock} testid="insight-missed">
        <div style={{ width: "100%" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
            <MiniStat label="FLARES THIS MONTH" value={`${ins.flaresThisMonth}`} testid="insight-flares-month" />
            <MiniStat label="AVG FLARE LENGTH" value={ins.avgFlareDurationHours != null ? fmtHours(ins.avgFlareDurationHours) : "—"} testid="insight-avg-flare-len" />
            <MiniStat label="MISSED DAYS" value={ins.missedDaysTotal ? `${ins.missedDaysTotal}` : "0"} testid="insight-missed-days" />
          </div>
          {Object.keys(ins.missedByActivity).length > 0 ? (
            <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 5 }}>
              {Object.entries(ins.missedByActivity).map(([k, v]) => (
                <span key={k} className="sym-tagchip" data-testid={`insight-missed-${k.replace(/\s+/g, "-").toLowerCase()}`}>{k} ×{v}</span>
              ))}
            </div>
          ) : (
            <div style={{ fontSize: 11, color: "var(--text-tertiary, #94A3B8)", marginTop: 10 }}>
              No missed work / PT / duty logged yet. Track it on flare entries — it is strong functional-impact evidence.
            </div>
          )}
        </div>
      </Card>

      <Card title="Medication effectiveness" sub="Did what you took actually help?" icon={Pill} testid="insight-meds">
        {ins.medEffectiveness.length === 0 ? (
          <Empty label="Log medications taken on entries (and whether they helped) to see what works." />
        ) : (
          <div className="sym-hbars">
            {ins.medEffectiveness.slice(0, 6).map((m) => (
              <div key={m.name} className="sym-hbar-row" data-testid={`insight-med-${m.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                <span className="sym-hbar-label">{m.name}</span>
                <div className="sym-hbar-track">
                  <div className="sym-hbar-fill" style={{ width: `${Math.round(m.score * 100)}%`, background: m.score >= 0.6 ? "var(--success, #1A8C52)" : m.score >= 0.3 ? "var(--gold-500, #C9A24A)" : "var(--error, #C72E2E)" }} />
                </div>
                <span className="sym-hbar-val mono">{Math.round(m.score * 100)}%</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card title="Sleep vs severity" sub="Does bad sleep predict worse days?" icon={Moon} testid="insight-sleep">
        {ins.sleepVsSeverity.every((b) => b.n === 0) ? (
          <Empty label='Set "Sleep quality" in the Factors section of an entry to unlock this correlation.' />
        ) : (
          <div className="sym-hbars">
            {ins.sleepVsSeverity.map((b) => (
              <div key={b.id} className="sym-hbar-row" data-testid={`insight-sleep-${b.id}`}>
                <span className="sym-hbar-label">{b.label}</span>
                <div className="sym-hbar-track">
                  <div className="sym-hbar-fill" style={{ width: b.avgSeverity != null ? `${(b.avgSeverity / 10) * 100}%` : "0%", background: sevColor(b.avgSeverity || 0) }} />
                </div>
                <span className="sym-hbar-val mono">{b.avgSeverity != null ? `${b.avgSeverity} avg` : "—"}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card title="Stress vs severity" sub="Does stress drive your symptoms?" icon={Lightning} testid="insight-stress">
        {ins.stressVsSeverity.every((b) => b.n === 0) ? (
          <Empty label='Set "Stress level" in the Factors section of an entry to unlock this correlation.' />
        ) : (
          <div className="sym-hbars">
            {ins.stressVsSeverity.map((b) => (
              <div key={b.id} className="sym-hbar-row" data-testid={`insight-stress-${b.id}`}>
                <span className="sym-hbar-label">{b.label}</span>
                <div className="sym-hbar-track">
                  <div className="sym-hbar-fill" style={{ width: b.avgSeverity != null ? `${(b.avgSeverity / 10) * 100}%` : "0%", background: sevColor(b.avgSeverity || 0) }} />
                </div>
                <span className="sym-hbar-val mono">{b.avgSeverity != null ? `${b.avgSeverity} avg` : "—"}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card title="Most affected body parts" sub="Where it hits, by entry count" icon={Person} testid="insight-bodyparts">
        {ins.bodyPartCounts.length === 0 ? (
          <Empty label="Tag body regions (Pro body map) or an affected joint in the condition tracker to see this." />
        ) : (
          <div className="sym-hbars">
            {ins.bodyPartCounts.slice(0, 6).map((b) => {
              const max = ins.bodyPartCounts[0].value || 1;
              return (
                <div key={b.label} className="sym-hbar-row" data-testid={`insight-bodypart-${b.label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                  <span className="sym-hbar-label">{b.label}</span>
                  <div className="sym-hbar-track">
                    <div className="sym-hbar-fill" style={{ width: `${(b.value / max) * 100}%`, background: "var(--brand, #1F3A6B)" }} />
                  </div>
                  <span className="sym-hbar-val mono">{b.value}x</span>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}

// ---- internals ----

function Card({ title, sub, icon: Icon, testid, children }) {
  return (
    <div className="sym-chart-card" data-testid={testid}>
      <div className="sym-chart-card-head">
        <Icon size={14} weight="bold" />
        <div>
          <div className="sym-chart-card-title">{title}</div>
          <div className="sym-chart-card-sub">{sub}</div>
        </div>
      </div>
      <div className="sym-chart-card-body">{children}</div>
    </div>
  );
}

function MonthBars({ rows }) {
  const max = Math.max(...rows.map((r) => r.count), 1);
  return (
    <div style={{ width: "100%" }}>
      <div className="sym-month-bars">
        {rows.map((r) => (
          <div key={r.key} className="sym-month-bar" data-testid={`insight-flare-month-${r.key}`}>
            <span className="sym-month-bar-val">{r.count > 0 ? r.count : ""}</span>
            <div className="sym-month-bar-fill" style={{ height: `${Math.max(3, (r.count / max) * 64)}px`, opacity: r.count > 0 ? 0.9 : 0.15 }} />
            <span className="sym-month-bar-label">{r.label}</span>
          </div>
        ))}
      </div>
      {rows.every((r) => r.count === 0) && (
        <div style={{ fontSize: 11, color: "var(--text-tertiary, #94A3B8)", marginTop: 8 }}>
          No flare-ups logged in the last 6 months. Use "Log flare-up now" when one hits.
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, testid }) {
  return (
    <div style={{ background: "var(--surface-2, #F8FAFC)", border: "1px solid var(--border, #E2E8F0)", borderRadius: 10, padding: 10 }} data-testid={testid}>
      <div style={{ fontSize: 8.5, fontWeight: 800, letterSpacing: "0.08em", color: "var(--text-tertiary, #94A3B8)" }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 800, fontFamily: "JetBrains Mono, monospace", marginTop: 3 }}>{value}</div>
    </div>
  );
}

function Empty({ label }) {
  return <div className="sym-chart-empty">{label}</div>;
}

function trendIcon(direction) {
  if (direction === "worsening") return TrendUp;
  if (direction === "improving") return TrendDown;
  return Minus;
}
function trendIconEl(direction) {
  const I = trendIcon(direction);
  return <I size={13} weight="bold" />;
}
function trendLabel(direction) {
  if (direction === "worsening") return "WORSENING";
  if (direction === "improving") return "IMPROVING";
  if (direction === "stable") return "STABLE";
  return "NOT ENOUGH DATA";
}
function sevColor(s) {
  if (s >= 7) return "var(--error, #C72E2E)";
  if (s >= 4) return "var(--gold-500, #C9A24A)";
  return "var(--success, #1A8C52)";
}
function shortDate(iso) {
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" }); } catch { return iso; }
}
